// 7s loop for the "Earn 2.68% APY on your deposits" card (design handoff:
// interest-landing-animation.md). The APY pill taps, a coin travels a dashed
// elbow path into the bank tile, the balance counts up and an interest chip
// pops in. The handoff specs CSS keyframes; they are sampled here instead so
// every part (and the balance counter) stays on one clock. Pure and testable.

export const INTEREST_LOOP_SECONDS = 7;
export const INTEREST_BASE_BALANCE = 212_875;
export const INTEREST_GAIN = 504.11;
/** Reduced-motion end state: path drawn, balance counted up, chip visible. */
export const INTEREST_REST_SECONDS = 4.5;

/** Stage geometry (px): the connector elbows down from the pill's edge node. */
export const INTEREST_STAGE = {
  width: 540,
  height: 206,
  pillWidth: 120,
  nodeX: 420,
  nodeY: 29,
  elbowX: 396,
  landY: 124,
  tileTop: 110,
} as const;

const { nodeX, nodeY, elbowX, landY } = INTEREST_STAGE;
export const INTEREST_PATH = `M${nodeX - 5} ${nodeY} L${nodeX - 12} ${nodeY} Q${elbowX} ${nodeY} ${elbowX} ${nodeY + 12} L${elbowX} ${landY}`;

const clamp = (x: number) => Math.min(1, Math.max(0, x));
const ease = (x: number) => 1 - (1 - clamp(x)) ** 3;

/** CSS-style cubic-bezier timing function (Newton + bisection). */
const bez = (a: number, b: number, s: number) =>
  3 * a * (1 - s) ** 2 * s + 3 * b * (1 - s) * s ** 2 + s ** 3;

function cubicBezier(x1: number, y1: number, x2: number, y2: number) {
  return (x: number) => {
    const target = clamp(x);
    let lo = 0;
    let hi = 1;
    let s = target;
    for (let i = 0; i < 24; i++) {
      const cx = bez(x1, x2, s);
      if (Math.abs(cx - target) < 1e-5) break;
      if (cx < target) lo = s;
      else hi = s;
      s = (lo + hi) / 2;
    }
    return bez(y1, y2, s);
  };
}
const easeInOut = cubicBezier(0.42, 0, 0.58, 1);
const coinEase = cubicBezier(0.4, 0, 0.2, 1);
const linear = (x: number) => x;

type Stop = readonly [percent: number, value: number];

/** Samples keyframes [percent, value], easing between adjacent stops. */
function sample(
  percent: number,
  stops: readonly Stop[],
  easing: (x: number) => number
) {
  const first = stops[0]!;
  if (percent <= first[0]) return first[1];
  for (let i = 1; i < stops.length; i++) {
    const [p1, v1] = stops[i]!;
    const [p0, v0] = stops[i - 1]!;
    if (percent <= p1) {
      return v0 + (v1 - v0) * easing((percent - p0) / (p1 - p0));
    }
  }
  return stops[stops.length - 1]![1];
}

// Path samples, so the coin can sit at a distance along the elbow.
const PATH_POINTS = (() => {
  const points: { x: number; y: number }[] = [];
  const start = { x: nodeX - 5, y: nodeY };
  const corner = { x: nodeX - 12, y: nodeY };
  const control = { x: elbowX, y: nodeY };
  const curveEnd = { x: elbowX, y: nodeY + 12 };
  points.push(start, corner);
  for (let i = 1; i <= 16; i++) {
    const s = i / 16;
    const a = (1 - s) ** 2;
    const b = 2 * (1 - s) * s;
    const c = s * s;
    points.push({
      x: a * corner.x + b * control.x + c * curveEnd.x,
      y: a * corner.y + b * control.y + c * curveEnd.y,
    });
  }
  points.push({ x: elbowX, y: landY });
  return points;
})();

const PATH_LENGTHS = PATH_POINTS.reduce<number[]>((acc, point, i) => {
  const prev = PATH_POINTS[i - 1];
  acc.push(
    i === 0 || !prev
      ? 0
      : acc[i - 1]! + Math.hypot(point.x - prev.x, point.y - prev.y)
  );
  return acc;
}, []);

export function getPointAtFraction(fraction: number) {
  const total = PATH_LENGTHS[PATH_LENGTHS.length - 1]!;
  const target = clamp(fraction) * total;
  for (let i = 1; i < PATH_POINTS.length; i++) {
    const end = PATH_LENGTHS[i]!;
    if (target <= end) {
      const begin = PATH_LENGTHS[i - 1]!;
      const f = end === begin ? 0 : (target - begin) / (end - begin);
      const a = PATH_POINTS[i - 1]!;
      const b = PATH_POINTS[i]!;
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
    }
  }
  return PATH_POINTS[PATH_POINTS.length - 1]!;
}

export interface InterestFrame {
  pillScale: number;
  /** Stroke dashoffset of the blue draw path (1 = hidden, 0 = drawn). */
  pathOffset: number;
  coin: { x: number; y: number; opacity: number; scale: number };
  ripple: { scale: number; opacity: number };
  chip: { opacity: number; y: number; scale: number };
  balance: number;
}

export function getInterestFrame(elapsedSeconds: number): InterestFrame {
  const t = elapsedSeconds % INTEREST_LOOP_SECONDS;
  const pct = (t / INTEREST_LOOP_SECONDS) * 100;

  const distance = sample(
    pct,
    [
      [14, 0],
      [44, 1],
    ],
    coinEase
  );
  const coinPoint = getPointAtFraction(distance);

  const balance =
    t < 3.1
      ? INTEREST_BASE_BALANCE
      : t < 4.1
        ? INTEREST_BASE_BALANCE + ease(t - 3.1) * INTEREST_GAIN
        : t < 6
          ? INTEREST_BASE_BALANCE + INTEREST_GAIN
          : INTEREST_BASE_BALANCE +
            INTEREST_GAIN -
            ease((t - 6) / 0.6) * INTEREST_GAIN;

  return {
    pillScale: sample(
      pct,
      [
        [10, 1],
        [13, 0.95],
        [17, 1],
      ],
      easeInOut
    ),
    pathOffset: sample(
      pct,
      [
        [14, 1],
        [42, 0],
        [70, 0],
        [84, 1],
      ],
      linear
    ),
    coin: {
      ...coinPoint,
      opacity: sample(
        pct,
        [
          [14, 0],
          [18, 1],
          [44, 1],
          [48, 0],
        ],
        coinEase
      ),
      scale: sample(
        pct,
        [
          [14, 0.6],
          [18, 1],
          [44, 1],
          [48, 0.4],
        ],
        coinEase
      ),
    },
    ripple: {
      scale: sample(
        pct,
        [
          [44, 0.4],
          [62, 1.5],
        ],
        ease
      ),
      opacity: sample(
        pct,
        [
          [44, 0],
          [46, 0.6],
          [62, 0],
        ],
        ease
      ),
    },
    chip: {
      opacity: sample(
        pct,
        [
          [45, 0],
          [50, 1],
          [82, 1],
          [88, 0],
        ],
        ease
      ),
      y: sample(
        pct,
        [
          [45, 8],
          [50, 0],
          [82, 0],
          [88, -6],
        ],
        ease
      ),
      scale: sample(
        pct,
        [
          [45, 0.9],
          [50, 1],
          [82, 1],
          [88, 0.96],
        ],
        ease
      ),
    },
    balance,
  };
}
